/*
 * MC_Confirm — DAO-FOUND-03
 *
 * Controlled confirmation dialog using DS Dialog primitives.
 * Used for vote + execute confirmations in the proposals feature.
 *
 * ESC key triggers onCancel via native Radix Dialog behavior.
 * Controlled: open state managed externally.
 */

import { useTranslations } from "next-intl";
import type { ComponentProps } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

type ButtonColors = NonNullable<ComponentProps<typeof Button>["colors"]>;

interface MC_ConfirmProps {
  open: boolean;
  title: string;
  body: string;
  confirmLabel?: string;
  cancelLabel?: string;
  // Color of the primary action — defaults to secondary.
  confirmColors?: ButtonColors;
  // When true: disable both buttons, swap confirm label for a spinner, and
  // prevent Escape/backdrop-click from dismissing the dialog. Used while a
  // confirmed mutation (vote / execute) is in flight so the user knows the
  // click registered and can't double-fire or escape mid-tx.
  confirmLoading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export const MC_Confirm: React.FC<MC_ConfirmProps> = ({
  open,
  title,
  body,
  confirmLabel,
  cancelLabel,
  confirmColors = "secondary",
  confirmLoading = false,
  onConfirm,
  onCancel,
}) => {
  const t = useTranslations("dao");
  const resolvedConfirmLabel = confirmLabel ?? t("proposals.shared.confirm");
  const resolvedCancelLabel = cancelLabel ?? t("proposals.shared.cancel");
  return (
    <Dialog
      open={open}
      onOpenChange={(val) => {
        // Don't dismiss while a mutation is in flight — the user already
        // confirmed, the chain is processing, closing now would orphan their
        // perception of what happened.
        if (!val && !confirmLoading) onCancel();
      }}
    >
      {/* DialogContent ships w-full + max-w-lg with no horizontal margin —
          on phones it touches the viewport edges. Cap it at "viewport minus
          2rem" below the `sm` breakpoint so there's always a gutter, and
          restore the original max-w-lg from sm: up. */}
      <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-lg">
        {/* `sm:text-center` is NOT redundant — it overrides shadcn's default
            `sm:text-left` in dialog.tsx:77 so the title stays centered at
            desktop breakpoints under RTL. twMerge resolves utilities at the
            same breakpoint, so dropping it lets `sm:text-left` win. */}
        <DialogHeader className="text-center sm:text-center">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{body}</DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex flex-row gap-2 sm:space-x-0">
          <Button
            type="button"
            variant="solid"
            colors={confirmColors}
            loading={confirmLoading}
            onClick={onConfirm}
            className="flex-1"
          >
            {resolvedConfirmLabel}
          </Button>
          <Button
            type="button"
            variant="bordered"
            colors="secondary"
            disabled={confirmLoading}
            onClick={onCancel}
            className="flex-1"
          >
            {resolvedCancelLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
